import { NEXT_PUBLIC_BASEURL, NEXT_PUBLIC_URL_WITHOUTHTTP } from "./constants"
import { getUTMDataFromCookies, type UTMParams } from "./utm-utils"

// App domain configuration
const APP_DOMAIN = NEXT_PUBLIC_BASEURL ?? 'https://app.hypertask.ai'
const APP_DOMAIN_HOSTNAME = NEXT_PUBLIC_URL_WITHOUTHTTP ?? 'app.hypertask.ai'
const LOGIN_PATH = '/login'

// Base login URL for server components
export const APP_LOGIN_URL = `${APP_DOMAIN}${LOGIN_PATH}`

/**
 * Checks if a URL belongs to the app.hypertask.ai domain
 */
export function isAppHypertaskUrl(url: string): boolean {
  try {
    // Handle relative URLs
    if (!url.startsWith('http')) {
      return false
    }
    const urlObj = new URL(url)
    return urlObj.hostname === APP_DOMAIN_HOSTNAME || urlObj.hostname === 'app.hypertask.ai'
  } catch {
    return false
  }
}

/**
 * Centralized utility to inject PostHog session ID and UTM parameters into app.hypertask.ai URLs
 * This should be used whenever redirecting to the app.hypertask.ai domain
 * 
 * @param url - The URL to enhance (can be full URL or path)
 * @param posthogInstance - Optional PostHog instance (from usePostHog hook). If not provided, will try to get from window.posthog
 * @returns Enhanced URL with ph_session_id parameter and UTM parameters from cookies
 */
export function enhanceAppHypertaskUrl(
  url: string,
  posthogInstance?: { get_session_id?: () => string | undefined | null }
): string {
  // Check if URL is for app.hypertask.ai domain
  if (!isAppHypertaskUrl(url)) {
    return url
  }

  try {
    const urlObj = new URL(url)
    
    // Get PostHog session ID
    let sessionId: string | undefined | null = null
    
    if (posthogInstance?.get_session_id) {
      sessionId = posthogInstance.get_session_id()
    } else if (typeof window !== 'undefined' && (window as any).posthog?.get_session_id) {
      sessionId = (window as any).posthog.get_session_id()
    }
    
    // Add session ID to URL if available
    if (sessionId) {
      urlObj.searchParams.set('ph_session_id', sessionId)
    }
    
    // Add UTM parameters from cookies
    try {
      const utmParams = getUTMDataFromCookies();
      
      // Add each UTM parameter to the URL's search params
      Object.entries(utmParams).forEach(([key, value]) => {
        if (value && typeof value === 'string') {
          urlObj.searchParams.set(key, value);
        }
      });
    } catch (error) {
      console.warn('Failed to add UTM parameters to app.hypertask.ai URL:', error);
    }
    
    return urlObj.toString()
  } catch (error) {
    console.warn('Failed to enhance app.hypertask.ai URL:', error)
    return url
  }
}
